import React, { useState, useEffect } from 'react';
import { useAuthenticator } from "@aws-amplify/ui-react";
import { useGetUserSliceQuery } from '../../redux/thunks/users/getuserslice';
import { useUpdatePrivacySettingsMutation } from '../../redux/thunks/users/updateprivacysettings';
import { useDeleteAccountMutation } from '../../redux/thunks/users/deleteaccount';
import EnhancedSEO from '../../Components/SEO/EnhancedSEO';



  // Privacy Controls Component (inline for simplicity - in production, this would be a separate file)
const PrivacyControls = ({ initialTab = 'settings', onClose }) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
    
  // Fetch user privacy settings from server
  const { 
    data: userPrivacySettings, 
    isError: isUserPrivacySettingsError, 
    isLoading: isUserPrivacySettingsLoading,
    error: userPrivacySettingsError,
    refetch: refetchPrivacySettings 
  } = useGetUserSliceQuery('privacySettings');

  // RTK mutation for updating privacy settings
  const [updatePrivacySettings, { 
    isLoading: isUpdating, 
    error: updateError 
  }] = useUpdatePrivacySettingsMutation();

  // RTK mutation for deleting account
  const [deleteAccount, { 
    isLoading: isDeletingAccount, 
    error: deleteAccountError 
  }] = useDeleteAccountMutation();


  // Default privacy settings structure
  const defaultPrivacySettings = {
    // Account & Profile Settings
    profileVisibility: 'public',
    displayNameVisibility: 'public',
    profilePhotoVisibility: 'public',
    contactInfoSharing: false,
    
    // Content & Interaction Controls
    reviewVisibility: 'public',
    businessFollowsVisibility: 'connections',
    endorsementsVisibility: 'public',
    trustNetworkVisibility: 'connections',
    communityVerificationParticipation: true,
    
    // AI & Data Processing Options
    aiAssistedReviews: true,
    personalizedRecommendations: true,
    smartRatingParticipation: true,
    locationBasedServices: true,
    
    // Communication Preferences
    emailNotifications: true,
    platformUpdates: true,
    marketingCommunications: false,
    followNotifications: true,
    reviewResponseNotifications: true,
    
    // Analytics & Data Usage
    analyticsParticipation: true,
    performanceMonitoring: true,
    abTestingParticipation: true,
    usageDataSharing: false
  };

  // Privacy settings state - initialize with defaults
  const [privacySettings, setPrivacySettings] = useState(defaultPrivacySettings);

  // Update privacy settings when server data is loaded
  useEffect(() => {
    if (userPrivacySettings?.privacySettings && !isUserPrivacySettingsLoading) {
      // Merge server data with defaults to ensure all fields are present
      setPrivacySettings(prevSettings => ({
        ...prevSettings,
        ...userPrivacySettings.privacySettings
      }));
    }
  }, [userPrivacySettings, isUserPrivacySettingsLoading]);

  const modalStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    zIndex: 1000,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '20px'
  };

  const contentStyle = {
    backgroundColor: 'white',
    borderRadius: '8px',
    maxWidth: '900px',
    maxHeight: '90vh',
    width: '100%',
    overflow: 'auto',
    position: 'relative'
  };

  const headerStyle = {
    background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
    color: 'white',
    padding: '24px',
    borderRadius: '8px 8px 0 0',
    position: 'sticky',
    top: 0,
    zIndex: 1001
  };

  const tabStyle = {
    display: 'flex',
    padding: '0 24px',
    borderBottom: '2px solid #e0e0e0',
    backgroundColor: 'white',
    position: 'sticky',
    top: '80px',
    zIndex: 1000
  };

  const tabButtonStyle = (isActive) => ({
    padding: '12px 24px',
    border: 'none',
    backgroundColor: 'transparent',
    color: isActive ? '#1976d2' : '#666',
    borderBottom: isActive ? '2px solid #1976d2' : '2px solid transparent',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: isActive ? '600' : '400',
    transition: 'all 0.2s'
  });

  const cardStyle = {
    backgroundColor: '#fff',
    padding: '24px',
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
    marginBottom: '16px'
  };

  const switchStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 0',
    borderBottom: '1px solid #f0f0f0'
  };

  const buttonStyle = {
    padding: '12px 24px',
    borderRadius: '4px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    margin: '4px',
    transition: 'all 0.2s'
  };

  const primaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#1976d2',
    color: 'white'
  };

  const dangerButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#d32f2f',
    color: 'white'
  };

  const outlinedButtonStyle = {
    ...buttonStyle,
    backgroundColor: 'transparent',
    color: '#1976d2',
    border: '1px solid #1976d2'
  };

  const alertStyle = {
    padding: '16px',
    borderRadius: '8px',
    marginBottom: '16px',
    border: '1px solid #ffebee',
    backgroundColor: '#ffebee',
    color: '#d32f2f'
  };

  const successAlertStyle = {
    padding: '16px',
    borderRadius: '8px',
    marginBottom: '16px',
    border: '1px solid #e8f5e8',
    backgroundColor: '#e8f5e8',
    color: '#388e3c'
  };

  const warningAlertStyle = {
    padding: '16px',
    borderRadius: '8px',
    marginBottom: '16px',
    border: '1px solid #fff3e0',
    backgroundColor: '#fff3e0',
    color: '#f57c00'
  };

  const loadingStyle = {
    padding: '16px',
    borderRadius: '8px',
    marginBottom: '16px',
    border: '1px solid #e3f2fd',
    backgroundColor: '#e3f2fd',
    color: '#1976d2',
    textAlign: 'center'
  };

  const generateDummyData = () => {
    return {
      account: {
        userId: 'user_12345',
        email: 'john.doe@example.com',
        displayName: 'John Doe',
        createdDate: '2024-01-15T10:30:00Z',
        lastLoginDate: '2025-06-05T14:22:00Z',
        trustScore: 85.5
      },
      reviews: [
        {
          reviewId: 'review_001',
          businessName: 'Joe\'s Pizza',
          rating: 5,
          reviewText: 'Amazing pizza! Great service and authentic Italian flavors.',
          dateCreated: '2024-11-20T18:45:00Z',
          helpful: 12
        },
        {
          reviewId: 'review_002',
          businessName: 'Main Street Bakery',
          rating: 4,
          reviewText: 'Fresh bread and pastries. Coffee could be better.',
          dateCreated: '2024-10-05T09:15:00Z',
          helpful: 8
        }
      ],
      businessInteractions: {
        following: [
          { businessName: 'Joe\'s Pizza', dateFollowed: '2024-11-20T18:50:00Z' },
          { businessName: 'Tech Repair Shop', dateFollowed: '2024-09-12T14:30:00Z' }
        ],
        endorsed: [
          { businessName: 'Joe\'s Pizza', dateEndorsed: '2024-11-21T10:00:00Z' }
        ]
      },
      trustNetwork: {
        connections: [
          { displayName: 'Sarah Johnson', connectionDate: '2024-03-10T12:00:00Z' },
          { displayName: 'Mike Chen', connectionDate: '2024-05-22T15:30:00Z' }
        ]
      },
      privacySettings: userPrivacySettings?.privacySettings || privacySettings,
      exportInfo: {
        generatedDate: new Date().toISOString(),
        dataVersion: '1.0'
      }
    };
  };

  const handleDownloadData = () => {
    setIsDownloading(true);
    
    // TODO: Replace with actual API call
    setTimeout(() => {
      const dummyData = generateDummyData();
      const dataStr = JSON.stringify(dummyData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `revfer-data-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setIsDownloading(false);
    }, 2000);
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation === 'DELETE MY ACCOUNT') {
      try {
        // Console log the deletion request
        console.log('Account deletion request being sent to server');
        
        // Call the RTK mutation to delete account
        const response = await deleteAccount({
          payload: { confirmationText: deleteConfirmation }
        }).unwrap();
        
        console.log('Account deletion response:', response);
        
        alert('Account deletion request submitted successfully. You will receive a confirmation email within 24 hours.');
        setShowDeleteWarning(false);
        setDeleteConfirmation('');
        onClose();
      } catch (error) {
        console.error('Error deleting account:', error);
        alert(error?.data?.message || 'Error processing account deletion. Please try again or contact support.');
      }
    } else {
      alert('Please type "DELETE MY ACCOUNT" exactly to confirm.');
    }
  };

  const handleSettingChange = (category, setting, value) => {
    setPrivacySettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  const handleSavePrivacySettings = async () => {
    setIsSaving(true);
    setSaveMessage('');
    
    try {
      // Console log the privacy settings before sending
      console.log('Privacy settings being sent to server:', privacySettings);
      
      // Call the RTK mutation to update privacy settings
      const response = await updatePrivacySettings({
        payload: privacySettings
      }).unwrap();
      
      console.log('Privacy settings update response:', response);
      
      setSaveMessage('Privacy settings saved successfully!');
      setIsSaving(false);
      
      // Auto-hide success message after 3 seconds
      setTimeout(() => setSaveMessage(''), 3000);
      
      // Optionally refetch the data to ensure consistency
      refetchPrivacySettings();
    } catch (error) {
      console.error('Error saving privacy settings:', error);
      setSaveMessage(error?.data?.message || 'Error saving settings. Please try again.');
      setIsSaving(false);
    }
  };

  const renderLoadingState = () => (
    <div style={loadingStyle}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
        <div style={{
          width: '20px',
          height: '20px',
          border: '2px solid #1976d2',
          borderTop: '2px solid transparent',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <span>Loading your privacy settings...</span>
      </div>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );

  const renderErrorState = () => (
    <div style={alertStyle}>
      <h4 style={{ margin: '0 0 8px 0' }}>Unable to Load Privacy Settings</h4>
      <p style={{ margin: '0 0 8px 0', fontSize: '14px' }}>
        {userPrivacySettingsError?.message || 'There was an error loading your privacy settings.'}
      </p>
      <button 
        style={outlinedButtonStyle}
        onClick={() => refetchPrivacySettings()}
      >
        Try Again
      </button>
    </div>
  );

  const renderPrivacySettings = () => {
    // Show loading state while fetching data
    if (isUserPrivacySettingsLoading) {
      return (
        <div style={{ padding: '24px' }}>
          {renderLoadingState()}
        </div>
      );
    }

    // Show error state if there's an error
    if (isUserPrivacySettingsError) {
      return (
        <div style={{ padding: '24px' }}>
          {renderErrorState()}
        </div>
      );
    }

    return (
      <div style={{ padding: '24px' }}>
        {/* Data Source Indicator */}
        <div style={{ ...successAlertStyle, marginBottom: '24px' }}>
          <h4 style={{ margin: '0 0 8px 0' }}>
            Current Settings Loaded
          </h4>
          <p style={{ margin: 0, fontSize: '14px' }}>
            These are your current privacy settings. Make any changes below and click "Save" to update them.
          </p>
        </div>

        <div style={cardStyle}>
          <h3 style={{ color: '#1976d2', marginBottom: '16px' }}>Account & Profile Settings</h3>
          
          <div style={switchStyle}>
            <div>
              <strong>Profile Visibility</strong>
              <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>Who can see your profile information</p>
            </div>
            <select 
              value={privacySettings.profileVisibility}
              onChange={(e) => handleSettingChange('account', 'profileVisibility', e.target.value)}
              style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            >
              <option value="public">Everyone</option>
              <option value="connections">My Connections</option>
              <option value="private">Only Me</option>
            </select>
          </div>

          <div style={switchStyle}>
            <div>
              <strong>Display Name Visibility</strong>
              <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>Show your name on reviews and interactions</p>
            </div>
            <select 
              value={privacySettings.displayNameVisibility}
              onChange={(e) => handleSettingChange('account', 'displayNameVisibility', e.target.value)}
              style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            >
              <option value="public">Everyone</option>
              <option value="connections">My Connections</option>
              <option value="private">Anonymous</option>
            </select>
          </div>

          <div style={switchStyle}>
            <div>
              <strong>Contact Information Sharing</strong>
              <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>Allow businesses to contact you directly</p>
            </div>
            <input 
              type="checkbox" 
              checked={privacySettings.contactInfoSharing}
              onChange={(e) => handleSettingChange('account', 'contactInfoSharing', e.target.checked)}
            />
          </div>
        </div>

        <div style={cardStyle}>
          <h3 style={{ color: '#388e3c', marginBottom: '16px' }}>AI & Data Processing Options</h3>
          
          <div style={switchStyle}>
            <div>
              <strong>AI-Assisted Review Writing</strong>
              <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>Use AI to help write detailed reviews</p>
            </div>
            <input 
              type="checkbox" 
              checked={privacySettings.aiAssistedReviews}
              onChange={(e) => handleSettingChange('ai', 'aiAssistedReviews', e.target.checked)}
            />
          </div>

          <div style={switchStyle}>
            <div>
              <strong>Personalized Recommendations</strong>
              <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>Get AI-powered business recommendations</p>
            </div>
            <input 
              type="checkbox" 
              checked={privacySettings.personalizedRecommendations}
              onChange={(e) => handleSettingChange('ai', 'personalizedRecommendations', e.target.checked)}
            />
          </div>

          <div style={switchStyle}>
            <div>
              <strong>Smart Rating Algorithm</strong>
              <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>Include your data in trust-weighted ratings</p>
            </div>
            <input 
              type="checkbox" 
              checked={privacySettings.smartRatingParticipation}
              onChange={(e) => handleSettingChange('ai', 'smartRatingParticipation', e.target.checked)}
            />
          </div>

          <div style={switchStyle}>
            <div>
              <strong>Location-Based Services</strong>
              <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>Use location for business recommendations</p>
            </div>
            <input 
              type="checkbox" 
              checked={privacySettings.locationBasedServices}
              onChange={(e) => handleSettingChange('ai', 'locationBasedServices', e.target.checked)}
            />
          </div>
        </div>

        <div style={cardStyle}>
          <h3 style={{ color: '#f57c00', marginBottom: '16px' }}>Communication Preferences</h3>
          
          <div style={switchStyle}>
            <div>
              <strong>Email Notifications</strong>
              <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>Receive important account and security emails</p>
            </div>
            <input 
              type="checkbox" 
              checked={privacySettings.emailNotifications}
              onChange={(e) => handleSettingChange('communication', 'emailNotifications', e.target.checked)}
            />
          </div>

          <div style={switchStyle}>
            <div>
              <strong>Marketing Communications</strong>
              <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>Receive promotional emails and offers</p>
            </div>
            <input 
              type="checkbox" 
              checked={privacySettings.marketingCommunications}
              onChange={(e) => handleSettingChange('communication', 'marketingCommunications', e.target.checked)}
            />
          </div>

          <div style={switchStyle}>
            <div>
              <strong>Analytics Participation</strong>
              <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>Help improve the platform with anonymous usage data</p>
            </div>
            <input 
              type="checkbox" 
              checked={privacySettings.analyticsParticipation}
              onChange={(e) => handleSettingChange('analytics', 'analyticsParticipation', e.target.checked)}
            />
          </div>
        </div>

        {/* Save Button and Status */}
        <div style={{ textAlign: 'center', marginTop: '24px' }}>
          {saveMessage && (
            <div style={saveMessage.includes('Error') ? alertStyle : successAlertStyle}>
              {saveMessage}
            </div>
          )}
          
          <button 
            style={{
              ...primaryButtonStyle,
              opacity: isSaving || isUpdating ? 0.6 : 1,
              cursor: isSaving || isUpdating ? 'not-allowed' : 'pointer'
            }}
            onClick={handleSavePrivacySettings}
            disabled={isSaving || isUpdating}
          >
            {isSaving || isUpdating ? 'Saving Settings...' : 'Save Privacy Settings'}
          </button>
          
          <p style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
            Changes take effect immediately after saving.
          </p>
        </div>
      </div>
    );
  };

  const renderDownloadData = () => (
    <div style={{ padding: '24px' }}>
      <div style={successAlertStyle}>
        <h4 style={{ margin: '0 0 8px 0' }}>Download Your Data</h4>
        <p style={{ margin: 0, fontSize: '14px' }}>
          Get a complete copy of all your data stored on Revfer in JSON format. This includes your account information, 
          reviews, business interactions, trust network data, and privacy settings.
        </p>
      </div>

      <div style={cardStyle}>
        <h3 style={{ marginBottom: '16px', color: '#1976d2' }}>What's Included in Your Data Export</h3>
        <ul style={{ paddingLeft: '20px' }}>
          <li><strong>Account Information:</strong> Profile data, verification status, trust score</li>
          <li><strong>Reviews & Ratings:</strong> All reviews you've written with timestamps and engagement metrics</li>
          <li><strong>Business Interactions:</strong> Businesses you follow, refer, and have asked questions about</li>
          <li><strong>Trust Network:</strong> Your connections and verification history</li>
          <li><strong>Privacy Settings:</strong> Your current privacy and communication preferences</li>
          <li><strong>AI Usage Data:</strong> History of AI features you've used (anonymized)</li>
        </ul>
      </div>

      <div style={cardStyle}>
        <h3 style={{ marginBottom: '16px', color: '#388e3c' }}>Data Export Information</h3>
        <p style={{ marginBottom: '12px' }}>
          <strong>Format:</strong> JSON (JavaScript Object Notation) - readable by most applications
        </p>
        <p style={{ marginBottom: '12px' }}>
          <strong>Processing Time:</strong> Immediate for standard exports
        </p>
        <p style={{ marginBottom: '12px' }}>
          <strong>Security:</strong> All exports are encrypted and require authentication to download
        </p>
      </div>

      <div style={{ textAlign: 'center', marginTop: '24px' }}>
        <button 
          style={primaryButtonStyle} 
          onClick={handleDownloadData}
          disabled={isDownloading}
        >
          {isDownloading ? 'Preparing Download...' : 'Download My Data'}
        </button>
        <p style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
          By downloading your data, you agree that this export is for personal use only.
        </p>
      </div>
    </div>
  );

  const renderDeleteAccount = () => (
    <div style={{ padding: '24px' }}>
      <div style={alertStyle}>
        <h4 style={{ margin: '0 0 8px 0' }}>⚠️ Permanent Account Deletion</h4>
        <p style={{ margin: 0, fontSize: '14px' }}>
          This action cannot be undone. Please read the information below carefully before proceeding.
        </p>
      </div>

      <div style={cardStyle}>
        <h3 style={{ marginBottom: '16px', color: '#d32f2f' }}>What Will Be Deleted</h3>
        <ul style={{ paddingLeft: '20px', color: '#d32f2f' }}>
          <li>Your account and profile information</li>
          <li>All reviews and ratings you've written</li>
          <li>Your trust network connections</li>
          <li>Business follows and endorsements</li>
          <li>Questions and community contributions</li>
          <li>AI usage history and preferences</li>
          <li>All personal data and activity history</li>
        </ul>
      </div>

      <div style={cardStyle}>
        <h3 style={{ marginBottom: '16px', color: '#f57c00' }}>What Will Be Retained</h3>
        <ul style={{ paddingLeft: '20px' }}>
          <li>Anonymized analytics data (no personal identifiers)</li>
          <li>Aggregate community verification contributions</li>
          <li>Legal records as required by law (up to 7 years)</li>
          <li>Fraud prevention data (hashed, non-identifiable)</li>
        </ul>
        <p style={{ fontSize: '14px', color: '#666', marginTop: '12px' }}>
          Retained data cannot be used to identify you and helps maintain platform integrity.
        </p>
      </div>

      {!showDeleteWarning ? (
        <div style={{ textAlign: 'center', marginTop: '24px' }}>
          <button 
            style={dangerButtonStyle}
            onClick={() => setShowDeleteWarning(true)}
          >
            I Want to Delete My Account
          </button>
        </div>
      ) : (
        <div style={cardStyle}>
          <h3 style={{ marginBottom: '16px', color: '#d32f2f' }}>Final Confirmation Required</h3>
          
          {/* Show loading state during account deletion */}
          {isDeletingAccount && (
            <div style={{ ...loadingStyle, marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
                <div style={{
                  width: '20px',
                  height: '20px',
                  border: '2px solid #d32f2f',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                <span>Processing account deletion request...</span>
              </div>
            </div>
          )}
          
          <p style={{ marginBottom: '16px' }}>
            To confirm account deletion, please type <strong>"DELETE MY ACCOUNT"</strong> in the box below:
          </p>
          <input 
            type="text"
            value={deleteConfirmation}
            onChange={(e) => setDeleteConfirmation(e.target.value)}
            placeholder="Type: DELETE MY ACCOUNT"
            disabled={isDeletingAccount}
            style={{ 
              width: '100%', 
              padding: '12px', 
              border: '2px solid #d32f2f', 
              borderRadius: '4px',
              fontSize: '16px',
              marginBottom: '16px',
              opacity: isDeletingAccount ? 0.6 : 1,
              cursor: isDeletingAccount ? 'not-allowed' : 'text'
            }}
          />
          <p style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
            This action will permanently delete your account within 24 hours. You will receive a final confirmation email.
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <button 
              style={{
                ...outlinedButtonStyle,
                opacity: isDeletingAccount ? 0.6 : 1,
                cursor: isDeletingAccount ? 'not-allowed' : 'pointer'
              }}
              onClick={() => {
                setShowDeleteWarning(false);
                setDeleteConfirmation('');
              }}
              disabled={isDeletingAccount}
            >
              Cancel
            </button>
            <button 
              style={{
                ...dangerButtonStyle,
                opacity: isDeletingAccount ? 0.6 : 1,
                cursor: isDeletingAccount ? 'not-allowed' : 'pointer'
              }}
              onClick={handleDeleteAccount}
              disabled={deleteConfirmation !== 'DELETE MY ACCOUNT' || isDeletingAccount}
            >
              {isDeletingAccount ? 'Deleting Account...' : 'Delete My Account Forever'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
  return (
    <div style={modalStyle} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={contentStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h1 style={{ margin: 0, fontSize: '32px', fontWeight: '300' }}>
                🛡️ Privacy Controls
              </h1>
              <p style={{ margin: '8px 0 0 0', fontSize: '16px', opacity: 0.9 }}>
                Manage your privacy settings, download your data, or delete your account
              </p>
            </div>
            <button 
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                fontSize: '24px',
                cursor: 'pointer',
                padding: '8px'
              }}
            >
              ×
            </button>
          </div>
        </div>

        <div style={tabStyle}>
          <button 
            style={tabButtonStyle(activeTab === 'settings')}
            onClick={() => setActiveTab('settings')}
          >
            Privacy Settings
          </button>
          <button 
            style={tabButtonStyle(activeTab === 'download')}
            onClick={() => setActiveTab('download')}
          >
            Download Data
          </button>
          <button 
            style={tabButtonStyle(activeTab === 'delete')}
            onClick={() => setActiveTab('delete')}
          >
            Delete Account
          </button>
        </div>

        {activeTab === 'settings' && renderPrivacySettings()}
        {activeTab === 'download' && renderDownloadData()}
        {activeTab === 'delete' && renderDeleteAccount()}
      </div>
    </div>
  );
};

const PrivacyPolicyPage = () => {
  const [expandedSection, setExpandedSection] = useState('overview');
  const [showSection, setShowSection] = useState(false);
  const [activePrivacyControl, setActivePrivacyControl] = useState(null); // 'settings', 'download', 'delete', or null

  const { authStatus } = useAuthenticator(context => [context.authStatus]);
  const isAuthenticated = authStatus === 'authenticated';

  const handleAccordionToggle = (sectionId) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };

  const containerStyle = {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '32px 16px',
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    lineHeight: 1.5,
    color: '#212121'
  };

  const headerStyle = {
    background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
    color: 'white',
    padding: '32px',
    borderRadius: '8px',
    marginBottom: '32px',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
  };

  const paperStyle = {
    backgroundColor: '#fff',
    padding: '16px',
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
    marginBottom: '16px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
  };

  const accordionStyle = {
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    marginBottom: '8px',
    overflow: 'hidden'
  };

  const accordionHeaderStyle = {
    padding: '16px',
    cursor: 'pointer',
    backgroundColor: '#f5f5f5',
    borderBottom: '1px solid #e0e0e0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    transition: 'background-color 0.2s'
  };

  const accordionContentStyle = {
    padding: '24px',
    borderTop: '1px solid #e0e0e0'
  };

  const chipStyle = {
    display: 'inline-block',
    padding: '6px 12px',
    margin: '4px',
    borderRadius: '16px',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.2s'
  };

  const cardStyle = {
    backgroundColor: '#f5f5f5',
    padding: '16px',
    borderRadius: '8px',
    marginBottom: '16px',
    border: '1px solid #e0e0e0'
  };

  const alertStyle = {
    padding: '16px',
    borderRadius: '8px',
    marginBottom: '16px',
    border: '1px solid #e3f2fd',
    backgroundColor: '#e3f2fd',
    color: '#1976d2'
  };

  const warningAlertStyle = {
    ...alertStyle,
    border: '1px solid #fff3e0',
    backgroundColor: '#fff3e0',
    color: '#f57c00'
  };

  const buttonStyle = {
    padding: '12px 24px',
    borderRadius: '4px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    margin: '4px',
    transition: 'all 0.2s'
  };

  const primaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#1976d2',
    color: 'white'
  };

  const outlinedButtonStyle = {
    ...buttonStyle,
    backgroundColor: 'transparent',
    color: '#1976d2',
    border: '1px solid #1976d2'
  };

  const sections = [
    {
      id: 'overview',
      title: 'Overview & Your Rights',
      icon: '🛡️',
      color: '#1976d2',
      content: (
        <div>
          <p style={{ marginBottom: '16px' }}>
            At Revfer, we're committed to protecting your privacy and being transparent about how we collect, use, and protect your personal information. This Privacy Policy explains our practices for our community-driven business review and referral platform.
          </p>
          
          <div style={alertStyle}>
            <h4 style={{ margin: '0 0 8px 0', fontSize: '16px' }}>
              Last Updated: June 4, 2025
            </h4>
            <p style={{ margin: 0, fontSize: '14px' }}>
              We regularly update this policy to reflect changes in our practices and legal requirements. Material changes will be communicated via email and platform notifications.
            </p>
          </div>

          <h3 style={{ marginTop: '24px', marginBottom: '16px', color: '#1976d2' }}>
            Your Privacy Rights Include:
          </h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginTop: '16px' }}>
            <div style={{ ...cardStyle, backgroundColor: '#e3f2fd', color: '#1976d2' }}>
              <h4 style={{ margin: '0 0 8px 0' }}>Right to Access</h4>
              <p style={{ margin: 0, fontSize: '14px' }}>Request copies of your personal data</p>
            </div>
            <div style={{ ...cardStyle, backgroundColor: '#f3e5f5', color: '#7b1fa2' }}>
              <h4 style={{ margin: '0 0 8px 0' }}>Right to Correction</h4>
              <p style={{ margin: 0, fontSize: '14px' }}>Update or correct your information</p>
            </div>
            <div style={{ ...cardStyle, backgroundColor: '#e8f5e8', color: '#388e3c' }}>
              <h4 style={{ margin: '0 0 8px 0' }}>Right to Deletion</h4>
              <p style={{ margin: 0, fontSize: '14px' }}>Request deletion of your data</p>
            </div>
            <div style={{ ...cardStyle, backgroundColor: '#fff3e0', color: '#f57c00' }}>
              <h4 style={{ margin: '0 0 8px 0' }}>Right to Portability</h4>
              <p style={{ margin: 0, fontSize: '14px' }}>Export your data in a readable format</p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'collection',
      title: 'Information We Collect',
      icon: '💾',
      color: '#7b1fa2',
      content: (
        <div>
          <p style={{ marginBottom: '16px' }}>
            We collect information to provide you with a trusted, personalized experience on our platform. Here's what we collect and why:
          </p>

          <div style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
              <span style={{ fontSize: '20px', marginRight: '8px' }}>👥</span>
              <h4 style={{ margin: 0, color: '#1976d2' }}>Account Information</h4>
            </div>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li>Name, email address, phone number, profile photo</li>
              <li>Authentication credentials and verification status</li>
              <li>Account preferences and privacy settings</li>
              <li>Premium membership status and billing information</li>
            </ul>
          </div>

          <div style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
              <span style={{ fontSize: '20px', marginRight: '8px' }}>🏢</span>
              <h4 style={{ margin: 0, color: '#7b1fa2' }}>Business Interaction Data</h4>
            </div>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li>Reviews you write, ratings you provide, and photos you upload</li>
              <li>Businesses you follow, refer, or interact with</li>
              <li>Questions you ask and answers you provide in Q&A sections</li>
              <li>Verification activities and community contributions</li>
            </ul>
          </div>

          <div style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
              <span style={{ fontSize: '20px', marginRight: '8px' }}>📍</span>
              <h4 style={{ margin: 0, color: '#388e3c' }}>Location & Usage Data</h4>
            </div>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li>General location (city/region) for business recommendations</li>
              <li>Search queries and business discovery patterns</li>
              <li>Platform usage analytics and feature engagement</li>
              <li>Device information and browser data for security</li>
            </ul>
          </div>

          <div style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
              <span style={{ fontSize: '20px', marginRight: '8px' }}>🔗</span>
              <h4 style={{ margin: 0, color: '#f57c00' }}>Network & Trust Data</h4>
            </div>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li>Your connection relationships with other users</li>
              <li>Trust chain information and verification history</li>
              <li>Referral patterns and network engagement metrics</li>
              <li>Business relationship verification status</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'ai-processing',
      title: 'AI & Data Processing',
      icon: '🧠',
      color: '#388e3c',
      content: (
        <div>
          <div style={warningAlertStyle}>
            <h4 style={{ margin: '0 0 8px 0' }}>
              Important: AI Processing Notice
            </h4>
            <p style={{ margin: 0, fontSize: '14px' }}>
              Revfer uses artificial intelligence to enhance your experience. Here's how your data may be processed by AI systems.
            </p>
          </div>

          <div style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
              <span style={{ fontSize: '20px', marginRight: '8px' }}>🧠</span>
              <h4 style={{ margin: 0, color: '#1976d2' }}>AI-Assisted Review Generation</h4>
            </div>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li>We use OpenAI's GPT models to help you write comprehensive reviews</li>
              <li>Your input prompts and generated content are processed by OpenAI</li>
              <li>We don't store your raw prompts, only the final review you choose to publish</li>
              <li>You maintain full control over what content gets published</li>
            </ul>
          </div>

          <div style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
              <span style={{ fontSize: '20px', marginRight: '8px' }}>☁️</span>
              <h4 style={{ margin: 0, color: '#7b1fa2' }}>Document Processing & Embeddings</h4>
            </div>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li>Business documents (PDFs, etc.) are processed into searchable chunks</li>
              <li>Reviews and business information are converted to vector embeddings</li>
              <li>These embeddings enable semantic search and intelligent recommendations</li>
              <li>Embeddings are mathematical representations that don't contain readable text</li>
            </ul>
          </div>

          <div style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
              <span style={{ fontSize: '20px', marginRight: '8px' }}>⭐</span>
              <h4 style={{ margin: 0, color: '#388e3c' }}>Smart Rating Algorithms</h4>
            </div>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li>Our algorithms analyze reviewer credibility and connection strength</li>
              <li>Trust network data helps weight review authenticity</li>
              <li>Community verification patterns inform our trust scoring</li>
              <li>All algorithmic processing maintains user privacy and anonymity</li>
            </ul>
          </div>

          <div style={alertStyle}>
            <p style={{ margin: 0, fontSize: '14px' }}>
              <strong>Your Control:</strong> You can opt out of AI-assisted features in your privacy settings. 
              This may limit some platform functionality but preserves your preference for minimal AI interaction.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'usage',
      title: 'How We Use Your Information',
      icon: '⚙️',
      color: '#f57c00',
      content: (
        <div>
          <p style={{ marginBottom: '16px' }}>
            We use your information to provide, improve, and protect our services. Here are the specific ways we use your data:
          </p>

          <div style={{ ...cardStyle, backgroundColor: '#e3f2fd', color: '#1976d2' }}>
            <h4 style={{ marginBottom: '12px' }}>Core Platform Services</h4>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li>Maintain your account and authenticate your identity</li>
              <li>Display your reviews, ratings, and business interactions</li>
              <li>Enable business search and location-based recommendations</li>
              <li>Process premium membership features and billing</li>
              <li>Facilitate communication between users and businesses</li>
            </ul>
          </div>

          <div style={{ ...cardStyle, backgroundColor: '#f3e5f5', color: '#7b1fa2' }}>
            <h4 style={{ marginBottom: '12px' }}>Trust & Safety</h4>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li>Verify review authenticity and detect fraudulent activity</li>
              <li>Maintain trust networks and connection verification</li>
              <li>Prevent spam, abuse, and platform manipulation</li>
              <li>Monitor compliance with community guidelines</li>
              <li>Protect user safety and business integrity</li>
            </ul>
          </div>

          <div style={{ ...cardStyle, backgroundColor: '#e8f5e8', color: '#388e3c' }}>
            <h4 style={{ marginBottom: '12px' }}>Personalization & Recommendations</h4>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li>Suggest relevant businesses based on your preferences and location</li>
              <li>Curate your "For You" feed with personalized content</li>
              <li>Recommend connections within your trust network</li>
              <li>Optimize search results for your interests and behavior</li>
              <li>Highlight trending businesses in your area</li>
            </ul>
          </div>

          <div style={{ ...cardStyle, backgroundColor: '#fff3e0', color: '#f57c00' }}>
            <h4 style={{ marginBottom: '12px' }}>Communication & Support</h4>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li>Send important account and security notifications</li>
              <li>Provide customer support and resolve issues</li>
              <li>Share platform updates and new feature announcements</li>
              <li>Send marketing communications (with your consent)</li>
              <li>Facilitate community events and engagement programs</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'sharing',
      title: 'Information Sharing & Disclosure',
      icon: '🔗',
      color: '#d32f2f',
      content: (
        <div>
          <div style={warningAlertStyle}>
            <h4 style={{ margin: '0 0 8px 0' }}>
              We Don't Sell Your Personal Data
            </h4>
            <p style={{ margin: 0, fontSize: '14px' }}>
              Revfer does not sell, rent, or trade your personal information to third parties for marketing purposes.
            </p>
          </div>

          {/* ===== SMS NOTIFICATIONS — add after the "We Don't Sell" alert ===== */}
          <div style={{ ...cardStyle, backgroundColor: '#e8f5e8', color: '#388e3c' }}>
            <h4 style={{ marginBottom: '12px' }}>SMS Notifications</h4>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>

              <li>
                 SMS notifications are an optional feature and
                  are not required to use any Revfer service.
              </li>
              <li>
                If you are a Premium subscriber and opt in to SMS notifications,
                we collect your mobile phone number solely to send you transactional
                notifications about your business content drafts on Revfer.
              </li>
              <li>
                We do not sell, rent, share or transfer your mobile phone number
                to third parties for marketing purposes.
              </li>
              <li>
                Your phone number is verified via a one-time passcode before SMS
                notifications are enabled.
              </li>
              <li>
                Message frequency varies based on your business activity,
                typically 1 to 3 messages per week.
              </li>
              <li>
                Message and data rates may apply. Reply STOP to opt out at any
                time or visit your notification settings at revfer.com/business/edit.
                Reply HELP for assistance.
              </li>
            </ul>
          </div>

          <h3 style={{ marginTop: '24px', marginBottom: '16px', color: '#1976d2' }}>
            When We Share Information:
          </h3>

          <div style={cardStyle}>
            <h4 style={{ marginBottom: '12px', color: '#1976d2' }}>
              Public Information by Design
            </h4>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li>Your reviews, ratings, and business references are public by default</li>
              <li>Your display name and profile photo appear with your contributions</li>
              <li>Business follow lists and some network connections may be visible</li>
              <li>Community verification activities are public to maintain transparency</li>
            </ul>
          </div>

          <div style={cardStyle}>
            <h4 style={{ marginBottom: '12px', color: '#7b1fa2' }}>
              Service Providers & Partners
            </h4>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li><strong>OpenAI:</strong> AI processing for review assistance and embeddings</li>
              <li><strong>AWS:</strong> Cloud hosting, storage, and serverless computing</li>
              <li><strong>MongoDB Atlas:</strong> Database services and vector search</li>
              <li><strong>Payment Processors:</strong> Billing and subscription management</li>
              <li><strong>Analytics Providers:</strong> Platform performance and usage insights</li>
            </ul>
          </div>

          <div style={cardStyle}>
            <h4 style={{ marginBottom: '12px', color: '#388e3c' }}>
              Business Integrations
            </h4>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li>Businesses you review may see your review content and rating</li>
              <li>QR code scans may be tracked for business analytics</li>
              <li>Verified business connections appear in trust chains</li>
              <li>Business owners can see aggregate engagement metrics</li>
            </ul>
          </div>

          <div style={{ ...cardStyle, backgroundColor: '#ffebee', color: '#d32f2f' }}>
            <h4 style={{ marginBottom: '12px' }}>
              Legal Requirements
            </h4>
            <p style={{ marginBottom: '8px' }}>
              We may disclose information when required by law, court order, or to:
            </p>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li>Protect the rights and safety of our users and the public</li>
              <li>Prevent fraud, abuse, or violations of our Terms of Service</li>
              <li>Comply with law enforcement investigations</li>
              <li>Defend our legal rights and interests</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'security',
      title: 'Data Security & Protection',
      icon: '🔒',
      color: '#1976d2',
      content: (
        <div>
          <div style={{ ...cardStyle, backgroundColor: '#e3f2fd', color: '#1976d2' }}>
            <h4 style={{ marginBottom: '8px' }}>Our Security Commitment</h4>
            <p style={{ margin: 0, fontSize: '14px' }}>
              We implement industry-standard security measures to protect your personal information 
              from unauthorized access, disclosure, alteration, and destruction.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '24px' }}>
            <div style={cardStyle}>
              <h4 style={{ marginBottom: '8px', color: '#1976d2' }}>
                Data Encryption
              </h4>
              <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px' }}>
                <li>Data encrypted in transit (TLS 1.3)</li>
                <li>Database encryption at rest</li>
                <li>Secure API communications</li>
                <li>Encrypted file storage in AWS S3</li>
              </ul>
            </div>
            <div style={cardStyle}>
              <h4 style={{ marginBottom: '8px', color: '#7b1fa2' }}>
                Access Controls
              </h4>
              <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px' }}>
                <li>Multi-factor authentication</li>
                <li>Role-based access permissions</li>
                <li>Regular access audits</li>
                <li>Secure credential management</li>
              </ul>
            </div>
            <div style={cardStyle}>
              <h4 style={{ marginBottom: '8px', color: '#388e3c' }}>
                Infrastructure Security
              </h4>
              <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px' }}>
                <li>AWS security compliance</li>
                <li>Regular security assessments</li>
                <li>Automated threat monitoring</li>
                <li>Secure development practices</li>
              </ul>
            </div>
            <div style={cardStyle}>
              <h4 style={{ marginBottom: '8px', color: '#f57c00' }}>
                Data Backup & Recovery
              </h4>
              <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px' }}>
                <li>Automated daily backups</li>
                <li>Geographic data redundancy</li>
                <li>Disaster recovery procedures</li>
                <li>Business continuity planning</li>
              </ul>
            </div>
          </div>

          <div style={alertStyle}>
            <h4 style={{ margin: '0 0 8px 0' }}>
              Your Role in Security
            </h4>
            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px' }}>
              <li>Use strong, unique passwords for your account</li>
              <li>Enable two-factor authentication when available</li>
              <li>Keep your contact information up to date</li>
              <li>Report suspicious activity immediately</li>
              <li>Review your privacy settings regularly</li>
            </ul>
          </div>

          <div style={{ ...cardStyle, backgroundColor: '#fff3e0', color: '#f57c00' }}>
            <h4 style={{ marginBottom: '8px' }}>
              Security Incident Response
            </h4>
            <p style={{ margin: 0, fontSize: '14px' }}>
              In the unlikely event of a security breach affecting your personal information, 
              we will notify you within 72 hours via email and provide detailed information 
              about the incident, its impact, and steps we're taking to address it.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'controls',
      title: 'Your Privacy Controls',
      icon: '⚙️',
      color: '#7b1fa2',
      content: (
        <div>
          <p style={{ marginBottom: '16px' }}>
            You have extensive control over your privacy and data on Revfer. Here's how to manage your settings:
          </p>

          <div style={cardStyle}>
            <h4 style={{ marginBottom: '12px', color: '#1976d2' }}>
              Account & Profile Settings
            </h4>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li>Update your personal information anytime in Account Settings</li>
              <li>Control who can see your profile and review history</li>
              <li>Manage your display name and profile photo visibility</li>
              <li>Set preferences for follow notifications and updates</li>
              <li>Choose your preferred communication channels</li>
            </ul>
          </div>

          <div style={cardStyle}>
            <h4 style={{ marginBottom: '12px', color: '#7b1fa2' }}>
              Content & Interaction Controls
            </h4>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li>Edit or delete your reviews and ratings at any time</li>
              <li>Control visibility of your business follows and references</li>
              <li>Manage your trust network connections and verification status</li>
              <li>Set preferences for business recommendation algorithms</li>
              <li>Opt out of community verification activities</li>
            </ul>
          </div>

          <div style={cardStyle}>
            <h4 style={{ marginBottom: '12px', color: '#388e3c' }}>
              AI & Data Processing Options
            </h4>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li>Disable AI-assisted review generation features</li>
              <li>Opt out of personalized recommendations</li>
              <li>Control smart rating algorithm participation</li>
              <li>Manage location-based service preferences</li>
              <li>Request data export or deletion at any time</li>
            </ul>
          </div>

          <div style={{ ...alertStyle, backgroundColor: '#e8f5e8', color: '#388e3c', border: '1px solid #c8e6c9' }}>
            <h4 style={{ margin: '0 0 8px 0' }}>
              Easy Privacy Management
            </h4>
            <p style={{ margin: 0, fontSize: '14px' }}>
              Access all privacy controls in your Account Settings → Privacy & Data. 
              Changes take effect immediately, and you'll receive confirmation of any significant updates.
            </p>
          </div>

          {isAuthenticated && 
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '24px' }}>
          <button 
            style={primaryButtonStyle}
            onClick={() => {
              if (!isAuthenticated) {
                alert('Please log in to manage your privacy settings.');
                return;
              }
              setActivePrivacyControl('settings');
            }}
          >
            Manage Privacy Settings
          </button>
          <button 
            style={outlinedButtonStyle}
            onClick={() => {
              if (!isAuthenticated) {
                alert('Please log in to download your data.');
                return;
              }
              setActivePrivacyControl('download');
            }}
          >
            Download My Data
          </button>
          <button 
            style={{ ...outlinedButtonStyle, color: '#d32f2f', borderColor: '#d32f2f' }}
            onClick={() => {
              if (!isAuthenticated) {
                alert('Please log in to delete your account.');
                return;
              }
              setActivePrivacyControl('delete');
            }}
          >
            Delete Account
          </button>
        </div>
          
          
          }

          
        </div>
      )
    },
    {
      id: 'contact',
      title: 'Contact & Questions',
      icon: '📧',
      color: '#0288d1',
      content: (
        <div>
          <p style={{ marginBottom: '16px' }}>
            We're here to help with any privacy questions or concerns. Here's how to reach us:
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
            <div style={{ ...cardStyle, backgroundColor: '#e3f2fd', color: '#1976d2' }}>
              <h4 style={{ marginBottom: '8px' }}>General Privacy Questions</h4>
              <p style={{ margin: '0 0 8px 0', fontSize: '14px' }}>
                Email: privacy@revfer.com
              </p>
              <p style={{ margin: '0 0 8px 0', fontSize: '14px' }}>
                Response time: Within 48 hours
              </p>
              <p style={{ margin: 0, fontSize: '14px' }}>
                For general questions about our privacy practices and this policy.
              </p>
            </div>
            <div style={{ ...cardStyle, backgroundColor: '#f3e5f5', color: '#7b1fa2' }}>
              <h4 style={{ marginBottom: '8px' }}>Data Rights Requests</h4>
              <p style={{ margin: '0 0 8px 0', fontSize: '14px' }}>
                Email: dataprotection@revfer.com
              </p>
              <p style={{ margin: '0 0 8px 0', fontSize: '14px' }}>
                Response time: Within 30 days
              </p>
              <p style={{ margin: 0, fontSize: '14px' }}>
                For access, correction, deletion, or portability requests.
              </p>
            </div>
            <div style={{ ...cardStyle, backgroundColor: '#ffebee', color: '#d32f2f' }}>
              <h4 style={{ marginBottom: '8px' }}>Security Concerns</h4>
              <p style={{ margin: '0 0 8px 0', fontSize: '14px' }}>
                Email: security@revfer.com
              </p>
              <p style={{ margin: '0 0 8px 0', fontSize: '14px' }}>
                Response time: Within 24 hours
              </p>
              <p style={{ margin: 0, fontSize: '14px' }}>
                For security incidents, vulnerabilities, or urgent privacy concerns.
              </p>
            </div>
            <div style={{ ...cardStyle, backgroundColor: '#e8f5e8', color: '#388e3c' }}>
              <h4 style={{ marginBottom: '8px' }}>Business Inquiries</h4>
              <p style={{ margin: '0 0 8px 0', fontSize: '14px' }}>
                Email: business@revfer.com
              </p>
              <p style={{ margin: '0 0 8px 0', fontSize: '14px' }}>
                Response time: Within 72 hours
              </p>
              <p style={{ margin: 0, fontSize: '14px' }}>
                For business-related privacy questions and partnerships.
              </p>
            </div>
          </div>

          <div style={{ ...alertStyle, marginTop: '24px' }}>
            <h4 style={{ margin: '0 0 8px 0' }}>
              Legal Information
            </h4>
            <p style={{ margin: 0, fontSize: '14px' }}>
              <strong>Data Controller:</strong> Revfer Inc., 59 Grover Rd Ashland MA 01721<br />
              <strong>EU Representative:</strong> Available upon request for EU residents<br />
              <strong>Governing Law:</strong> This Privacy Policy is governed by the laws of Massachusetts, USA
            </p>
          </div>
        </div>
      )
    }
  ];

  return (
    <>
      {/* 🚀 COMPREHENSIVE SEO OPTIMIZATION WITH STRUCTURED DATA */}
      <EnhancedSEO 
        title="Privacy Policy - Revfer | Data Protection, AI Processing & User Rights"
        description="Comprehensive privacy policy for Revfer's AI business discovery platform. Learn how we protect your personal information, process data with AI, manage user privacy rights (GDPR/CCPA compliant), and maintain transparent data practices for our professional networking and business review community."
        keywords="privacy policy, data protection, user privacy rights, GDPR compliance, CCPA privacy rights, AI data processing, personal information protection, privacy settings, data security, professional networking privacy, business review platform privacy, user data rights, privacy controls, data deletion, information sharing policy, OpenAI data processing, vector embeddings privacy, trust network privacy, location data privacy, review data protection"
        type="webpage"
        image="/images/revfer-privacy-policy-og.jpg"
        author="Revfer Legal Team"
        dateModified="2025-06-04T00:00:00Z"
        datePublished="2024-01-15T00:00:00Z"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Legal', url: '/legal' },
          { name: 'Privacy Policy', url: '/legal/privacy-policy' }
        ]}
        organizationData={{
          "@context": "https://schema.org",
          "@type": ["TechnologyCompany", "LegalDocument"],
          "mainEntity": {
            "@type": "PrivacyPolicy",
            "name": "Revfer Privacy Policy",
            "description": "Comprehensive privacy policy covering data collection, AI processing, user rights, and privacy controls for Revfer's business discovery platform",
            "url": "https://www.revfer.com/legal/privacy-policy",
            "dateModified": "2025-06-04T00:00:00Z",
            "datePublished": "2024-01-15T00:00:00Z",
            "publisher": {
              "@type": "Organization",
              "name": "Revfer Inc",
              "url": "https://www.revfer.com",
              "logo": {
                "@type": "ImageObject",
                "url": "https://www.revfer.com/favicon.svg"
              }
            },
            "inLanguage": "en-US",
            "copyrightHolder": {
              "@type": "Organization",
              "name": "Revfer Inc"
            },
            "accountablePerson": {
              "@type": "ContactPoint",
              "contactType": "Data Protection Officer",
              "email": "privacy@revfer.com"
            },
            "about": [
              {
                "@type": "Thing",
                "name": "Data Collection Practices",
                "description": "Information about what personal data we collect and why"
              },
              {
                "@type": "Thing", 
                "name": "AI Data Processing",
                "description": "How artificial intelligence and machine learning process user data"
              },
              {
                "@type": "Thing",
                "name": "User Privacy Rights", 
                "description": "Rights to access, correct, delete, and port personal data"
              },
              {
                "@type": "Thing",
                "name": "Data Security Measures",
                "description": "Technical and organizational measures to protect personal information"
              },
              {
                "@type": "Thing",
                "name": "Information Sharing",
                "description": "When and how personal information may be shared with third parties"
              }
            ],
            "mentions": [
              {
                "@type": "SoftwareApplication",
                "name": "OpenAI GPT",
                "description": "AI models used for review assistance and content generation"
              },
              {
                "@type": "Organization",
                "name": "Amazon Web Services",
                "description": "Cloud infrastructure and hosting services"
              },
              {
                "@type": "Legislation",
                "name": "General Data Protection Regulation",
                "alternateName": "GDPR",
                "description": "EU privacy regulation compliance"
              },
              {
                "@type": "Legislation", 
                "name": "California Consumer Privacy Act",
                "alternateName": "CCPA",
                "description": "California privacy rights compliance"
              }
            ]
          },
          "name": "Revfer",
          "alternateName": "Revfer Inc",
          "description": "AI business discovery platform with comprehensive privacy protections and user data rights",
          "url": "https://www.revfer.com",
          "logo": {
            "@type": "ImageObject",
            "url": "https://www.revfer.com/favicon.svg",
            "caption": "Revfer AI Platform Logo"
          },
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "59 Grover Rd",
            "addressLocality": "Ashland",
            "addressRegion": "MA", 
            "postalCode": "01721",
            "addressCountry": "US"
          },
          "contactPoint": [
            {
              "@type": "ContactPoint",
              "contactType": "privacy inquiries",
              "email": "privacy@revfer.com",
              "availableLanguage": "English",
              "description": "General privacy questions and policy inquiries"
            },
            {
              "@type": "ContactPoint",
              "contactType": "data protection",
              "email": "dataprotection@revfer.com", 
              "availableLanguage": "English",
              "description": "Data rights requests - access, correction, deletion, portability"
            },
            {
              "@type": "ContactPoint",
              "contactType": "security",
              "email": "security@revfer.com",
              "availableLanguage": "English", 
              "description": "Security incidents and privacy concerns"
            },
            {
              "@type": "ContactPoint",
              "contactType": "customer service",
              "email": "hello@revfer.com",
              "availableLanguage": "English"
            }
          ],
          "sameAs": [
            "https://twitter.com/revfer",
            "https://linkedin.com/company/revfer"
          ],
          "knowsAbout": [
            "Data Privacy Rights",
            "GDPR Compliance",
            "CCPA Compliance", 
            "AI Data Processing",
            "Personal Information Protection",
            "User Privacy Controls",
            "Data Security",
            "Privacy by Design",
            "Transparent Data Practices",
            "Professional Networking Privacy"
          ],
          "privacyPolicy": "https://www.revfer.com/legal/privacy-policy",
          "termsOfService": "https://www.revfer.com/legal/terms-of-service",
          "compliance": [
            {
              "@type": "Thing",
              "name": "GDPR Compliance", 
              "description": "Full compliance with European Union General Data Protection Regulation"
            },
            {
              "@type": "Thing",
              "name": "CCPA Compliance",
              "description": "California Consumer Privacy Act compliance for US users"
            },
            {
              "@type": "Thing",
              "name": "SOC 2 Type II",
              "description": "Security and availability controls certification"
            }
          ],
          "dataProcessingActivities": [
            {
              "@type": "Thing",
              "name": "Business Review Processing",
              "description": "Collection and processing of user reviews and ratings"
            },
            {
              "@type": "Thing", 
              "name": "AI-Assisted Content Generation",
              "description": "Use of language models to help users create comprehensive reviews"
            },
            {
              "@type": "Thing",
              "name": "Trust Network Analysis", 
              "description": "Processing connection data to determine review credibility"
            },
            {
              "@type": "Thing",
              "name": "Personalized Recommendations",
              "description": "AI algorithms for business discovery and matching"
            }
          ]
        }}
        faqData={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What personal information does Revfer collect?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "We collect account information (name, email, profile photo), business interaction data (reviews, ratings, follows), location data for recommendations, and network/trust data for verification. All collection is transparent and purposeful for platform functionality."
              }
            },
            {
              "@type": "Question", 
              "name": "How does AI processing affect my privacy?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "We use AI for review assistance, business recommendations, and trust analysis. Your data may be processed by OpenAI for content generation, converted to vector embeddings for search, and analyzed by algorithms for personalization. You can opt out of AI features in privacy settings."
              }
            },
            {
              "@type": "Question",
              "name": "What are my privacy rights on Revfer?",
              "acceptedAnswer": {
                "@type": "Answer", 
                "text": "You have rights to access, correct, delete, and export your personal data. You can control privacy settings, manage AI processing preferences, and request account deletion. These rights are available immediately through your account settings or by contacting our privacy team."
              }
            },
            {
              "@type": "Question",
              "name": "Does Revfer sell my personal information?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "No, Revfer does not sell, rent, or trade your personal information to third parties for marketing purposes. We may share data with service providers (like OpenAI for AI processing) and as required by law, but never for commercial sale."
              }
            },
            {
              "@type": "Question",
              "name": "How can I delete my account and data?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "You can request account deletion through your privacy settings or by contacting dataprotection@revfer.com. Deletion is permanent and includes all reviews, connections, and personal data. Some anonymized data may be retained for legal compliance and platform integrity."
              }
            }
          ]
        }}
      />

      <div style={containerStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
            <span style={{ fontSize: '40px', marginRight: '16px' }}>🛡️</span>
            <h1 style={{ margin: 0, fontSize: '48px', fontWeight: '300' }}>
              Privacy Policy
            </h1>
          </div>
          <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '400', opacity: 0.9 }}>
            Your privacy matters. Learn how we protect and use your information on Revfer.
          </h2>
          <div style={{ 
            display: 'inline-block', 
            marginTop: '16px',
            padding: '8px 16px',
            backgroundColor: 'rgba(255,255,255,0.2)',
            borderRadius: '16px',
            fontSize: '14px'
          }}>
            Last Updated: June 4, 2025
          </div>
        </div>

        {/* Quick Navigation */}
        <div style={paperStyle}>
          <h3 style={{ marginBottom: '16px', color: '#1976d2' }}>
            Quick Navigation
          </h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {sections.map((section) => (
              <span
                key={section.id}
                style={{
                  ...chipStyle,
                  backgroundColor: expandedSection === section.id ? section.color : '#f5f5f5',
                  color: expandedSection === section.id ? 'white' : '#666',
                  border: expandedSection === section.id ? 'none' : '1px solid #e0e0e0'
                }}
                onClick={() => setExpandedSection(section.id)}
              >
                {section.icon} {section.title}
              </span>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div>
          {sections.map((section) => (
            <div key={section.id} style={accordionStyle}>
              <div
                style={{
                  ...accordionHeaderStyle,
                  backgroundColor: expandedSection === section.id ? '#f5f5f5' : '#fafafa'
                }}
                onClick={() => handleAccordionToggle(section.id)}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ fontSize: '24px', marginRight: '12px', color: section.color }}>
                    {section.icon}
                  </span>
                  <h3 style={{ margin: 0, fontSize: '20px' }}>
                    {section.title}
                  </h3>
                </div>
                <span style={{ fontSize: '20px', color: '#666' }}>
                  {expandedSection === section.id ? '−' : '+'}
                </span>
              </div>
              {expandedSection === section.id && (
                <div style={accordionContentStyle}>
                  {section.content}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer Actions */}
        {showSection &&
        <div style={{ ...paperStyle, backgroundColor: '#f5f5f5', marginTop: '32px' }}>
          <h3 style={{ marginBottom: '16px', color: '#1976d2' }}>
            Need Help?
          </h3>
          <p style={{ marginBottom: '16px' }}>
            If you have questions about this Privacy Policy or how we handle your information, 
            we're here to help.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            <button 
              style={primaryButtonStyle}
              onClick={() => window.location.href = 'mailto:privacy@revfer.com?subject=Privacy Policy Question'}
            >
              Contact Privacy Team
            </button>
            <button 
              style={outlinedButtonStyle}
              onClick={() => window.open('/legal/terms-of-service', '_blank')}
            >
              View Terms of Service
            </button>
            <button 
              style={outlinedButtonStyle}
              onClick={() => alert('PDF download will be available soon. You can print this page or save as PDF using your browser.')}
            >
              Download Policy PDF
            </button>
          </div>
        </div>
        }

        {/* Privacy Controls Modal */}
        {activePrivacyControl && (
          <PrivacyControls 
            initialTab={activePrivacyControl}
            onClose={() => setActivePrivacyControl(null)}
          />
        )}
        
      </div>
    </>
  );
};

export default PrivacyPolicyPage;